import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import Plot from 'react-plotly.js';
import { selectUser } from '../../reducers/userSlice';
import { chartColors } from '../../helpers/chartColors';
import { useTheme } from '@mui/styles';

export const RTFOPumpdownDeltaChart = ({ currentRTFO, gridView=true }) => {

  const theme = useTheme();
  const user = useSelector(selectUser);

  const darkMode = user?.value?.darkMode;

  const layout ={
    yaxis: { // delta isip y axis
      linecolor: darkMode ? 'lightgrey' : 'black',
      gridcolor: darkMode ? 'grey' : 'lightgrey',
      color: darkMode ? 'lightgrey' : 'black',
      title: {
        text: 'Delta Pressure (MPa)',
        font: {
          size: 12
        },
      },
    },
    xaxis: { // stage number x axis
      title: {
        text: 'Stage Number',
        font: {
          size: 12
        }
      },
      linecolor: darkMode ? 'lightgrey' : 'black',
      gridcolor: darkMode ? 'grey' : 'lightgrey',
      color: darkMode ? 'lightgrey' : 'black',
      zeroline: false,
      type: 'category'
    },
    hovermode: "closest",
    autosize: true,
    showlegend: true,
    legend: {
      orientation: "h",
      yanchor: "top",
      font: {
        color: darkMode ? 'lightgrey' : 'grey'
      },
      y: -0.2
    },
    shapes: [ 
      
    ],
    annotations: [
      {
        text: "Delta ISIP",
          font: {
          size: 14,
           color: darkMode ? 'lightgrey' : 'black'
        },
        showarrow: false,
        align: 'center',
        x: 0.5, //position in x domain
        y: (gridView ? 1.15 : 1.075), //position in y domain
        xref: 'paper',
        yref: 'paper',
      },
      {
        text: `Pad: ${currentRTFO?.name}`,
          font: {
          size: 10,
           color: darkMode ? 'lightgrey' : 'black'
        },
        showarrow: false,
        align: 'center',
        x: 0.5, //position in x domain
        y: (gridView ? 1.09 : 1.035), //position in y domain
        xref: 'paper',
        yref: 'paper',
      },
    ],
    uirevision: true,
    margin: {"t": 60, "l": 60, "r": 60, "b": 50},
    images: [
      {
        x: (gridView ? 0.05 : 0.035),
        y: (gridView ? 1.05 : 1.025),
        sizex: (gridView ? 0.1 : 0.05),
        sizey: 0.1,
        source: process.env.PUBLIC_URL + `/img/ssd_logomark_chart_${darkMode ? 'white' : 'primary'}.png`,
        xref: "page",
        yref: "page",
        xanchor: "right",
        yanchor: "bottom",
        opacity: 0.75
      }
    ],
    plot_bgcolor: "transparent",
    paper_bgcolor: theme?.palette?.elevationOne?.[(darkMode ? 'dark' : 'light')]
  };


  const data = useMemo(() => {
    try {
      // add traces
      let traces = [];

      const allWells = Object.keys(currentRTFO?.perfSchedule?.wells)?.sort((a, b) => a - b);
      const maxStageNumber = Math.max(...allWells?.map(wellName => parseInt(Object.keys(currentRTFO?.perfSchedule?.wells?.[wellName]?.stages)?.slice(-1)?.[0])));
      
      for (const well of allWells) {
        const X = Array.from(Array(maxStageNumber), (_, index) => parseInt(index) + 1);
        const Y = [];
        const wellIndex = allWells?.indexOf(well);
        const wellName = well.charAt(0)?.toUpperCase()?.concat(well?.substring(1, ));

        for (let i = 0; i < X?.length; i++) { // Stage numbers

          const stageName = `${currentRTFO?.name}_${wellName}_${i + 1}`;

          // const stageNames = wellStages?.filter(s => (Number(String(s.split('_')?.[2])?.replace(/[^0-9.]+|^0(?=[1-9])/g, '')?.trim())) === parseInt(X[i]));
          
          // let stageName = stageNames?.[0] || '';
          // if (stageNames?.length > 1) { // if repeat stages found
          //   const maxTotalProppant = Math.max(...stageNames?.map(name => currentRTFO?.stageData?.[name]?.metrics?.tpmCalc * currentRTFO?.stageData?.[name]?.metrics?.stageLength));
          //   stageName = stageNames?.find(s => (currentRTFO?.stageData?.[s]?.metrics?.tpmCalc *  currentRTFO?.stageData?.[s]?.metrics?.stageLength)?.toFixed(0) === maxTotalProppant?.toFixed(0));
          // };

          const stage = currentRTFO?.stageData?.[stageName];

          let yValue = (stage?.pumpdownParams?.postPerfISIP - stage?.pumpdownParams?.pumpdownISIP) || 0

          Y.push(Number(yValue) || 0);
        };

        traces.push(
          { // non-cumulative distribution
            x: X,
            y: Y,
            type: 'bar',
            xaxis: 'x1',
            yaxis: 'y1',
            name: wellName,
            marker: {
              size: 8,
              opacity: 1,
              color: chartColors[wellIndex]
            },
          }
        );
      };
      
      return traces;

    } catch (error) {
      console.log(error);
      return [];
    };
  }, [currentRTFO?.name, currentRTFO?.perfSchedule?.wells, currentRTFO?.stageData]);



  const config = {
    displaylogo: false,
    toImageButtonOptions: {
      format: 'png', // one of png, svg, jpeg, webp
      filename: "RTFO-Gutenberg-" + (new Date().toISOString().split('T')[0]),
      height: 550,
      width: 960,
      scale: 1 // Multiply title/legend/axis/canvas sizes by this factor
    }
  };

  try {
    return (
      <Plot
        data={data}
        layout={layout}
        config={config}
        useResizeHandler={true}
        style={{width: '100%', height: '100%'}}
      />
    );
    
  } catch (error) {
    return <></>;
  };

};

